import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Users, Briefcase, TrendingUp, Plus } from 'lucide-react';
import api, { jobsAPI } from '../../utils/api';

const CompanyDashboard = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({ users: 0, jobs: 0, active_jobs: 0 });
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [jobsRes, usersRes] = await Promise.all([
        jobsAPI.list(),
        api.get('/users/list')
      ]);

      const jobs = jobsRes.data;
      const users = usersRes.data;

      setStats({
        users: users.length,
        jobs: jobs.length,
        active_jobs: jobs.filter(j => ['in_progress', 'scheduled', 'accepted'].includes(j.status)).length
      });

      setRecentJobs(jobs.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="company-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold mb-2">{t('dashboard.title', 'Dashboard')}</h1>
        <p className="text-gray-600">{t('dashboard.welcome', 'Welcome to your control panel')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.total_users', 'Total Users')}</CardTitle>
            <Users className="text-primary" size={20} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.users}</div>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard.customers_staff', 'Customers & Staff')}</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.active_jobs', 'Active Jobs')}</CardTitle>
            <TrendingUp className="text-green-600" size={20} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.active_jobs}</div>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard.in_progress', 'In progress')}</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.total_jobs', 'Total Jobs')}</CardTitle>
            <Briefcase className="text-blue-600" size={20} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats.jobs}</div>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard.all_time', 'All time')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Jobs */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.recent_jobs', 'Recent Jobs')}</CardTitle>
          <CardDescription>{t('dashboard.latest_moving_jobs', 'Latest moving jobs')}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">{t('common.loading', 'Loading...')}</div>
          ) : recentJobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 mb-4">{t('dashboard.no_jobs_yet', 'No jobs yet')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentJobs.map((job) => (
                <div
                  key={job.id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                  data-testid={`job-${job.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{job.job_number}</h3>
                      <p className="text-sm text-gray-600">
                        {job.from_address.city} → {job.to_address.city}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(job.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                      {job.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanyDashboard;
